
import { jitsiLocalStorage } from '@jitsi/js-utils';

import { ReducerRegistry } from '../base/redux';

import {
    CLEAR_RECORDING_SESSIONS,
    LIVESTREAM_OBJECT,
    RECORDING_MODE,
    RECORDING_SESSION_UPDATED,
    RECORDING_STOP_CLEARED,
    RECORDING_STOP_REQUESTED,
    SET_AUTO_LIVE_STREAM_KEYS,
    SET_PENDING_RECORDING_NOTIFICATION_UID,
    SET_STREAM_KEY
} from './actionTypes';

declare var interfaceConfig: Object;


const DEFAULT_STATE = {
    pendingNotificationUids: {},
    sessionDatas: [],
    recordingMode: undefined,
    LiveStreamDataSaved: jitsiLocalStorage.getItem('liveStreamDetails') ?? null,

    LiveStream: navigator.product === 'ReactNative' ? {} : interfaceConfig.LIVE_STREAM_TOOLS,

    Recording: navigator.product === 'ReactNative' ? {} : interfaceConfig.RECORDING_TOOLS,
    streamObject: {},

    // Set of session IDs where the moderator requested a stop but the server
    // has not yet confirmed OFF. Used to suppress stale PENDING/ON XMPP events.
    stoppingSessionIds: new Set()
};


/**
 * The name of the Redux store this feature stores its state in.
 */
const STORE_NAME = 'features/recording';

/**
 * Reduces the Redux actions of the feature features/recording.
 */
ReducerRegistry.register(STORE_NAME,
    (state = DEFAULT_STATE, action) => {
        switch (action.type) {

        case CLEAR_RECORDING_SESSIONS:
            return {
                ...state,
                sessionDatas: [],
                stoppingSessionIds: new Set()
            };

        case RECORDING_SESSION_UPDATED:
            return {
                ...state,
                sessionDatas:
                    _updateSessionDatas(state.sessionDatas, action.sessionData)
            };

        case SET_PENDING_RECORDING_NOTIFICATION_UID: {
            const pendingNotificationUids = {
                ...state.pendingNotificationUids
            };

            pendingNotificationUids[action.streamType] = action.uid;

            return {
                ...state,
                pendingNotificationUids
            };
        }

        case RECORDING_MODE:
            return {
                ...state,
                recordingMode: action.mode
            };

        case SET_STREAM_KEY:
            return {
                ...state,
                streamKey: action.streamKey
            };

        case LIVESTREAM_OBJECT:
            return {
                ...state,
                streamObject: { ...state.streamObject,
                    ...action.data }
            };

        case SET_AUTO_LIVE_STREAM_KEYS:
            return {
                ...state,
                autoLiveStreamKeys: action.autoLiveStreamKeys
            };

        case RECORDING_STOP_REQUESTED: {
            // Add this session to the stopping set so middleware can ignore
            // stale PENDING/ON XMPP events from Jibri still transitioning.
            const nextStoppingIds = new Set(state.stoppingSessionIds);

            nextStoppingIds.add(action.sessionId);

            return {
                ...state,
                stoppingSessionIds: nextStoppingIds
            };
        }

        case RECORDING_STOP_CLEARED: {
            // Server confirmed OFF — remove the session from the stopping set.
            const clearedIds = new Set(state.stoppingSessionIds);

            clearedIds.delete(action.sessionId);

            return {
                ...state,
                stoppingSessionIds: clearedIds
            };
        }

        default:
            return state;
        }
    });

/**
 * Updates the known information on recording sessions.
 *
 * @param {Array} sessionDatas - The current sessions in the redux store.
 * @param {Object} newSessionData - The updated session data.
 * @private
 * @returns {Array} The session datas with the updated session data added.
 */
function _updateSessionDatas(sessionDatas, newSessionData) {
    const hasExistingSessionData = sessionDatas.find(
        sessionData => sessionData.id === newSessionData.id);
    let newSessionDatas;

    if (hasExistingSessionData) {
        newSessionDatas = sessionDatas.map(sessionData => {
            if (sessionData.id === newSessionData.id) {
                return {
                    ...newSessionData
                };
            }

            // Nothing to update for this session data so pass it back in.
            return sessionData;
        });
    } else {
        // If the session data is not present, then there is nothing to update
        // and instead it needs to be added to the known session datas.
        newSessionDatas = [
            ...sessionDatas,
            { ...newSessionData }
        ];
    }

    return newSessionDatas;
}
